/**
 * PieceSummaryDoc — the Piece Summary, read in the main window.
 *
 * The side column carries a card for this; opening it puts the summary where
 * a document goes, because that is the column's job: steer the main screen,
 * do not become the screen. Everything a person might want to check about
 * the piece itself is here in one document rather than as six collapsed
 * sections crowding a 344px strip:
 *
 *   - the characteristics table
 *   - the editorial direction it was written from
 *   - publication status and links
 *   - the token spend
 *
 * SEO fields ride along under their own heading. They are not in the
 * founder's list for this document, but they are the manual fallback from
 * BUG-045 (the SEO Optimizer computes these and historically did not persist
 * them) and the column has no SEO section any more — dropping the form would
 * quietly remove the only way to correct a score by hand.
 */

import type { PieceDetail, PieceDraft, PieceReport } from "@/types/piece";
import { PieceCharacteristics } from "./panels/ContentPanel";
import { PublishedPanel } from "./panels/PublishedPanel";
import { TokenUsagePanel } from "./panels/TokenUsagePanel";
import { SeoPanel } from "./panels/SeoPanel";

export interface PieceSummaryDocProps {
  piece: PieceDetail;
  latestDraft: PieceDraft | null;
  reports: PieceReport[];
}

export function PieceSummaryDoc({
  piece,
  latestDraft,
  reports,
}: PieceSummaryDocProps) {
  return (
    <div className="psummary">
      <section className="psummary-section">
        <h2 className="psummary-h">The piece</h2>
        <PieceCharacteristics piece={piece} latestDraft={latestDraft} />
      </section>

      <section className="psummary-section">
        <h2 className="psummary-h">Editorial direction</h2>
        {piece.editorial_direction ? (
          <p className="psummary-brief">{piece.editorial_direction}</p>
        ) : (
          <p className="psummary-empty">
            No editorial direction was given — the pipeline worked from the
            title alone.
          </p>
        )}
      </section>

      <section className="psummary-section">
        <h2 className="psummary-h">SEO &amp; scores</h2>
        <SeoPanel key={piece.id} piece={piece} reports={reports} hideReport />
      </section>

      <section className="psummary-section">
        <h2 className="psummary-h">Publication</h2>
        <PublishedPanel piece={piece} />
      </section>

      <section className="psummary-section">
        <h2 className="psummary-h">Token spend</h2>
        <TokenUsagePanel pieceId={piece.id} />
      </section>
    </div>
  );
}
